// The warehouse manager's "Taşı" and "Sayıyı düzelt" on the map: what the dialogs check before
// anything is sent, in the words the API would use. The API checks it all again.
import {
  BIN_ADJUST_REASON_LABELS,
  MIXED_BIN_QUESTION,
  PUTAWAY_REJECTION_LABELS,
  adjustReasonFits,
  mixedBinWarning,
  movableQty,
  normalizeScan,
  reservedOnShelf,
  shelfQty,
  type BinAdjustReason,
  type BinLineFigures,
  type MapBinDetail,
  type MapBinItem,
  type MixedBinDetails,
} from '@wms/domain';
import { readerTail } from './scan';

/** The details of a MIXED_BIN answer (the bin holds another seller's stock or another product), or null for any other. */
export function mixedBinOf(code: string | null | undefined, details: unknown): MixedBinDetails | null {
  if (code !== 'MIXED_BIN' || typeof details !== 'object' || details === null) return null;
  const d = details as Partial<MixedBinDetails>;
  if ((d.rejection !== 'other_seller' && d.rejection !== 'other_product') || typeof d.bin !== 'string') return null;
  return {
    rejection: d.rejection,
    rules: Array.isArray(d.rules) ? d.rules : [d.rejection],
    bin: d.bin,
    sellers: Array.isArray(d.sellers) ? d.sellers : [],
    skus: Array.isArray(d.skus) ? d.skus : [],
    capacity: d.capacity === 'volume_exceeded' || d.capacity === 'weight_exceeded' ? d.capacity : null,
  };
}

/** What the "Yine de bu göze koy" dialog says and sends. */
export interface MixedBinPrompt {
  readonly title: string;
  /** what lies in the bin, then the question */
  readonly lines: readonly string[];
  /** the confirming button's words; null: this person cannot put the goods there (the capacity is a supervisor's) */
  readonly confirm: string | null;
  /** the confirmation passes the recorded volume or load too (acceptCapacity) */
  readonly acceptCapacity: boolean;
}

/**
 * The warning before goods go into a bin holding another seller's stock (or another product where the
 * warehouse keeps products apart): anyone may confirm it with one press (no typing: a staff member
 * with a reader in hand presses the button). A recorded volume or load the goods exceed too goes in
 * the same confirmation for a supervisor; anyone else is told to call one.
 */
export function mixedBinPrompt(d: MixedBinDetails, opts: { readonly verb: 'koy' | 'taşı'; readonly supervisor: boolean }): MixedBinPrompt {
  const title = `${d.bin} gözünde ${d.rejection === 'other_seller' ? 'başka satıcının ürünü' : 'başka ürün'} var`;
  // another seller's: which of its products lie there (another product's warning names them already)
  const which = d.rejection === 'other_seller' && d.skus.length > 0 ? ` (${d.skus.slice(0, 3).join(', ')}${d.skus.length > 3 ? '…' : ''})` : '';
  const lines = [`${mixedBinWarning(d.rejection, d)}${which}.`];
  if (d.capacity) {
    const fit = `Ayrıca kayıtlı ölçülere göre ürün bu göze sığmıyor görünüyor (${PUTAWAY_REJECTION_LABELS[d.capacity].toLocaleLowerCase('tr')}).`;
    if (!opts.supervisor) {
      lines.push(`${fit} Buraya koymak için vardiya sorumlusu ya da depo yöneticisi onaylamalı.`);
      return { title, lines, confirm: null, acceptCapacity: false };
    }
    lines.push(`${fit} Ürünler sığıyorsa onaylayın: ikisi birlikte kayda geçer.`);
  }
  lines.push(opts.verb === 'koy' ? MIXED_BIN_QUESTION : 'Yine de bu göze taşı?');
  return { title, lines, confirm: `Yine de ${d.bin} gözüne ${opts.verb}`, acceptCapacity: d.capacity !== null };
}

/** A typed count: digits only ("12" → 12); empty or anything else → null. */
export function parseCount(text: string): number | null {
  const t = text.trim();
  return /^\d{1,7}$/.test(t) ? Number(t) : null;
}

/**
 * Why the units ordered from this bin stay where they are, for a move (they cannot go) or a
 * correction (the shelf keeps at least the ones still to pick); null when no order waits for any.
 */
export function reservedWords(f: BinLineFigures, action: 'move' | 'adjust'): string | null {
  if (f.reserved <= 0) return null;
  const waiting = reservedOnShelf(f);
  if (waiting <= 0) return `${f.reserved} adedi siparişlere ayrıldı ve toplandı, paketlenmeyi bekliyor (rafta değil).`;
  const shelf = f.picked > 0 ? `${f.picked} tanesi toplandı (rafta değil), ${waiting} tanesini toplayıcı bu gözden alacak` : 'toplayıcı onları bu gözden alacak';
  const rest = action === 'move' ? 'bunlar taşınamaz' : `gözde en az ${waiting} adet kalmalı`;
  return `${f.reserved} adedi siparişlere ayrıldı: ${shelf}, ${rest}.`;
}

/** What a person typed and a reader read into the "Taşınacak adet" box, told apart. */
export interface QtyBoxRead {
  /** the count the box keeps: what was typed before a code, else the count it held before */
  readonly qtyText: string;
  /** a bin's code or label read (or typed) after the count: the target; null when only a count came */
  readonly code: string | null;
}

/** Shortest code taken for a bin (a stray letter typed into the count is dropped) */
const MIN_CODE = 3;
const MAX_COUNT_DIGITS = 7;

/**
 * The "Taşınacak adet" box as it is left (Enter, or the cursor goes elsewhere). The manager types the
 * count and reads the target's label, and the cursor may still be in this box when the label is read:
 * a reader's run at the end (readerTail, as the mal kabul count box finds it), or anything from the
 * first letter on (a label, a code typed here by mistake), is the target bin, never a count of
 * millions. `log` holds the box's text after each change since it was last left, `value` its text now,
 * `before` the count it held then.
 */
export function readQtyBox(log: readonly { readonly value: string; readonly at: number }[], value: string, before: string): QtyBoxRead {
  // typed on after the count it held (not over it): the reader's run starts after that count
  const appended = before !== '' && log.length > 0 && log[0]!.value.length > before.length && log[0]!.value.startsWith(before);
  const changes = appended ? [{ value: before, at: Number.NEGATIVE_INFINITY }, ...log] : log;
  const letter = value.search(/[^\d\s]/);
  const cut = readerTail(changes, value) ?? (letter === -1 ? null : letter);
  if (cut === null) return { qtyText: value.replace(/\D/g, '').slice(0, MAX_COUNT_DIGITS), code: null };
  const head = value.slice(0, cut).trim();
  const code = normalizeScan(value.slice(cut));
  return { qtyText: /^\d+$/.test(head) ? head.slice(0, MAX_COUNT_DIGITS) : before, code: code.length >= MIN_CODE ? code : null };
}

/** What stops moving `qty` units of the line; null when they may go. */
export function moveQtyProblem(qty: number | null, f: BinLineFigures): string | null {
  const movable = movableQty(f);
  // read again after the panel was out of date: the line was emptied meanwhile
  if (f.onHand === 0) return 'Gözde bu üründen kalmadı.';
  if (movable === 0) return 'Taşınabilecek adet yok: gözdeki ürünlerin hepsi siparişlere ayrıldı.';
  if (qty === null || qty < 1) return 'Taşınacak adedi yazın.';
  if (qty > movable) return `En fazla ${movable} adet taşınabilir.`;
  return null;
}

export interface AdjustInput {
  readonly newQty: number | null;
  readonly reason: BinAdjustReason | null;
  readonly note: string;
}

/** What stops saving a correction; null when it may be saved. */
export function adjustProblem(input: AdjustInput, f: BinLineFigures): string | null {
  const least = reservedOnShelf(f);
  if (input.newQty === null) return 'Rafta saydığınız adedi yazın.';
  if (input.newQty < least) return `En az ${least} adet olmalı: ${least} adedi siparişlere ayrıldı ve bu gözden toplanacak. Rafta daha az varsa gözü sayıma alın.`;
  if (input.newQty === shelfQty(f)) return 'Adet aynı: değişiklik yok.';
  if (input.reason === null) return 'Nedeni seçin.';
  const difference = input.newQty - shelfQty(f);
  if (!adjustReasonFits(input.reason, difference)) {
    return `“${BIN_ADJUST_REASON_LABELS[input.reason]}” yalnız adet ${difference > 0 ? 'düşerken' : 'artarken'} seçilir: başka bir neden seçin.`;
  }
  if (input.reason === 'other' && !input.note.trim()) return '“Diğer” için açıklama yazın.';
  return null;
}

/** Whether a reason chip may be chosen for the count typed: one that says the opposite of the change is greyed out. */
export function reasonFitsCount(reason: BinAdjustReason, newQty: number | null, f: BinLineFigures): boolean {
  return newQty === null || adjustReasonFits(reason, newQty - shelfQty(f));
}

/** "3 adet eklenecek" / "2 adet düşülecek"; null while nothing changes. */
export function adjustDifference(newQty: number | null, f: BinLineFigures): string | null {
  if (newQty === null) return null;
  const difference = newQty - shelfQty(f);
  if (difference === 0) return null;
  return difference > 0 ? `${difference} adet eklenecek` : `${-difference} adet düşülecek`;
}

/**
 * Refusals that say the shelf panel's figures are out of date (a count, a pick, a putaway or a new
 * reservation since it was read): the panel reads the bin again and the dialog works on the new figures.
 */
const STALE_CODES: ReadonlySet<string> = new Set([
  'STOCK_NOT_FOUND',
  'STOCK_CHANGED',
  'STOCK_BUSY',
  'RESERVED_NOT_MOVABLE',
  'MOVE_QTY',
  'NO_CHANGE',
  'BELOW_RESERVED',
  'BIN_LOCKED',
  'BIN_INACTIVE',
]);

export const staleRefusal = (code: string | null | undefined) => code !== null && code !== undefined && STALE_CODES.has(code);

/** The line a dialog works on as the shelf panel now has it; a line no longer listed was emptied (the map leaves out lines at 0). */
export function freshLine(bins: readonly MapBinDetail[], bin: MapBinDetail, item: MapBinItem): { readonly bin: MapBinDetail; readonly item: MapBinItem } {
  const now = bins.find((b) => b.code === bin.code);
  if (!now) return { bin, item };
  const line = now.items.find((i) => i.productId === item.productId && i.lotNo === item.lotNo && i.expiryDate === item.expiryDate);
  return { bin: now, item: line ?? { ...item, onHand: 0, reserved: 0, picked: 0 } };
}
